import React, { useRef, useState } from "react";

const CHANNEL_META = {
  instagram: { label: "Instagram", color: "#DC2743" },
  facebook: { label: "Facebook", color: "#1877F2" },
  website: { label: "Website", color: "#0284C7" },
};

/**
 * Multi-series SVG sparkline with hover tooltip.
 * Props:
 *  - byChannel: { instagram: number[], facebook: number[], website: number[] }
 *  - width, height
 */
export default function MultiSparkline({ byChannel = {}, width = 200, height = 48 }) {
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  // Filter to channels that have any non-zero data
  const series = Object.entries(byChannel)
    .filter(([, arr]) => Array.isArray(arr) && arr.some((v) => v > 0))
    .map(([key, data]) => ({ key, data, ...CHANNEL_META[key] }));

  if (!series.length) return null;

  const len = series[0].data.length;
  const max = Math.max(1, ...series.flatMap((s) => s.data));
  const padTop = 4;
  const padBottom = 4;
  const usable = height - padTop - padBottom;
  const stepX = len > 1 ? width / (len - 1) : width;

  const pathFor = (data) =>
    data
      .map((v, i) => {
        const x = i * stepX;
        const y = padTop + (usable - (v / max) * usable);
        return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(" ");

  const onMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relX = e.clientX - rect.left;
    const idx = Math.max(0, Math.min(len - 1, Math.round(relX / (rect.width / (len - 1)))));
    setHoverIdx(idx);
  };
  const onLeave = () => setHoverIdx(null);

  // Build tooltip date label: index 29 = today, 0 = 29 days ago
  let tooltipDate = "";
  if (hoverIdx !== null) {
    const d = new Date();
    d.setDate(d.getDate() - (len - 1 - hoverIdx));
    tooltipDate = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  return (
    <div ref={wrapRef} className="relative inline-block" onMouseMove={onMove} onMouseLeave={onLeave} style={{ width, height: height + 18 }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img">
        {/* Per-series lines */}
        {series.map((s) => (
          <path
            key={s.key}
            d={pathFor(s.data)}
            stroke={s.color}
            strokeWidth="1.5"
            fill="none"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={hoverIdx === null ? 1 : 0.85}
          />
        ))}
        {/* Hover guide */}
        {hoverIdx !== null && (
          <g>
            <line
              x1={hoverIdx * stepX}
              x2={hoverIdx * stepX}
              y1={0}
              y2={height}
              stroke="#1E3A8A"
              strokeWidth="1"
              strokeDasharray="2,2"
              opacity={0.4}
            />
            {series.map((s) => {
              const v = s.data[hoverIdx] || 0;
              const x = hoverIdx * stepX;
              const y = padTop + (usable - (v / max) * usable);
              return <circle key={s.key} cx={x} cy={y} r="2.5" fill={s.color} stroke="#fff" strokeWidth="1" />;
            })}
          </g>
        )}
      </svg>

      {/* Mini legend */}
      <div className="flex flex-wrap items-center gap-3 mt-1 text-[10px] text-neutral-500">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color }} /> {s.label}
          </span>
        ))}
      </div>

      {/* Tooltip */}
      {hoverIdx !== null && (
        <div
          className="absolute z-10 bg-navy text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none whitespace-nowrap"
          style={{
            left: Math.min(width - 120, Math.max(0, hoverIdx * stepX - 50)),
            top: -8,
            transform: "translateY(-100%)",
          }}
        >
          <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">{tooltipDate}</div>
          {series.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color }} />
                {s.label}
              </span>
              <strong>{s.data[hoverIdx] || 0}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
